import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import BACKEND_BASE from './config';
import { enrichGame, sortGames } from './hypeMetrics';
import { getTeamLogoUrl } from './teamLogos';
import './HypePreview.css';

const HypeContext = createContext({ games: [], loading: true });
const format = n => new Intl.NumberFormat('en-US').format(n);
export function upcomingHype(data, now) {
  return sortGames((data?.games || []).map(g => enrichGame(g, now, 'x'))
    .filter(g => !g.ended && Number.isFinite(g.count)), 'coverage');
}
export function HypePreviewProvider({ children }) {
  const [data, setData] = useState(null), [loading, setLoading] = useState(true);
  const [error, setError] = useState(false), [now, setNow] = useState(Date.now());
  useEffect(() => {
    const controller = new AbortController(); let active = true;
    async function load() {
      if (document.hidden) return;
      try {
        const res = await fetch(`${BACKEND_BASE}/hype?source=x`, { signal: controller.signal });
        if (!res.ok) throw new Error('Hype unavailable');
        const next = await res.json();
        if (active) { setData(next); setError(Boolean(next.error)); }
      } catch (e) { if (active && e.name !== 'AbortError') setError(true); }
      finally { if (active) { setNow(Date.now()); setLoading(false); } }
    }
    load(); const timer = setInterval(load, 300000);
    const clock = setInterval(() => setNow(Date.now()), 60000);
    document.addEventListener('visibilitychange', load);
    return () => { active = false; controller.abort(); clearInterval(timer); clearInterval(clock); document.removeEventListener('visibilitychange', load); };
  }, []);
  const games = useMemo(() => upcomingHype(data, now), [data, now]);
  return <HypeContext.Provider value={{ data, games, loading, error }}>{children}</HypeContext.Provider>;
}
function Logos({ game }) {
  return <span className="preview-logos" aria-hidden="true">{[game.team1, game.team2].map((t, i) => <img key={t} className={`${i ? 'second' : ''} ${t === 'Texas' ? 'wide' : ''}`} src={getTeamLogoUrl(t)} alt="" onError={e => { e.currentTarget.style.visibility = 'hidden'; }} />)}</span>;
}
function Change({ game, error }) {
  if (game.delayed || error) return <span className="preview-change muted">Delayed</span>;
  if (game.change == null) return <span className="preview-change muted">Awaiting trend</span>;
  return <span className={`preview-change ${game.change > 0 ? 'up' : game.change < 0 ? 'down' : 'muted'}`}>{game.change > 0 ? '+' : ''}{format(game.change)}<small> / 24h</small></span>;
}
const href = g => `/hype?source=x&game=${encodeURIComponent(g.id)}`;
export function HypeTicker() {
  const { data, games, loading, error } = useContext(HypeContext);
  const [paused, setPaused] = useState(false);
  if (!games.length) return null;
  const items = games.slice(0, 16);
  return <aside className={`hype-ticker ${paused ? 'is-paused' : ''}`} aria-label="Upcoming college football X discussion ticker">
    <Link to="/hype?source=x" className="ticker-label"><strong>Pregame hype</strong><span>Week {data?.week ?? '—'} · X posts / 24h</span></Link>
    <div className="ticker-window"><div className="ticker-track" style={{ '--ticker-duration': `${Math.max(40, items.length * 8)}s` }}>
      {[0, 1].map(copy => <div className="ticker-group" key={copy} aria-hidden={copy ? true : undefined}>{items.map(g => <Link to={href(g)} key={g.id} tabIndex={copy ? -1 : 0} className="ticker-item"><Logos game={g}/><span className="ticker-matchup">{g.team1} <span>vs</span> {g.team2}</span><strong>{format(g.count)}</strong><Change game={g} error={error || loading}/></Link>)}</div>)}
    </div></div>
    <button className="ticker-pause" onClick={() => setPaused(p => !p)} aria-label={paused ? 'Resume hype ticker' : 'Pause hype ticker'} aria-pressed={paused}>{paused ? <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m5 3 8 5-8 5Z" fill="currentColor"/></svg> : <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5 3v10M11 3v10" stroke="currentColor" strokeWidth="2"/></svg>}</button>
  </aside>;
}
export function HomeHypePreview() {
  const { data, games, loading, error } = useContext(HypeContext);
  const top = games.slice(0, 4), max = Math.max(1, ...top.map(g => g.count));
  return <section className="home-hype-preview" aria-labelledby="home-hype-title">
    <div className="preview-heading"><div><p>Week {data?.week ?? '—'} · College football</p><h3 id="home-hype-title">The week’s most talked-about games</h3><span>Matching X posts in the latest collected 24-hour window</span></div><Link to="/hype?source=x">Explore hype <span aria-hidden="true">→</span></Link></div>
    {top.length ? <div className="preview-games">{top.map((g, i) => <Link to={href(g)} className="preview-game" key={g.id}>
      <div className="preview-game-top"><span className="preview-rank">0{i + 1}</span><Logos game={g}/><span className="preview-network">{g.network || ''}</span></div>
      <h4>{g.team1}<span>vs {g.team2}</span></h4>
      <div className="preview-game-count"><strong>{format(g.count)}</strong><span>posts / 24h</span></div>
      <div className="preview-meter" aria-hidden="true"><span style={{ width: `${g.count / max * 100}%` }}/></div>
      <div className="preview-game-bottom"><Change game={g} error={error}/><span>{g.observedAt ? new Date(g.observedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Awaiting update'}</span></div>
    </Link>)}</div> : <p className="preview-empty">{loading ? 'Loading this week’s conversation…' : error ? 'Hype data is temporarily unavailable.' : 'No upcoming matchup readings yet. Check back as collection begins.'}</p>}
  </section>;
}
